import { lazy, Suspense, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  CircularProgress,
  Grid,
  Stack,
  Button,
  IconButton,
  Tooltip,
  Typography,
} from '@mui/material';
import dayjs from 'dayjs';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import useAuthStore from '../../store/authStore';
import { fetchTutorStudents } from '../../api/students';
import {
  fetchMySchedules,
  createScheduleSlot,
  updateScheduleSlot,
  deleteScheduleSlot,
} from '../../api/schedules';
import { fetchMyWeeklyReport, submitWeeklyReport } from '../../api/weeklyReports';
import ScheduleFormDialog from '../../components/calendar/ScheduleFormDialog';
import WeeklyReportPanel from '../../components/calendar/WeeklyReportPanel';
import { enqueueSnackbar } from 'notistack';
import ConfirmDialog from '../../components/feedback/ConfirmDialog';
import WeekSelector from '../../components/calendar/WeekSelector';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteSweepIcon from '@mui/icons-material/DeleteSweep';
import { formatWeekRangeLabel, getWeekStartDate } from '../../utils/week';

const WeeklyCalendar = lazy(() => import('../../components/calendar/WeeklyCalendar'));

const createEmptyWeeklyReport = () => ({
  lessonsLoadLevel: 50,
  preparationLoadLevel: 50,
  studentEngagementLevel: 50,
  attendanceSatisfactionLevel: 50,
  studentMotivationLevel: 50,
  wellbeingLevel: 50,
  attentionStudentIds: [],
  topStudentIds: [],
  weekMood: null,
  comment: '',
});

const TutorSchedulePage = () => {
  const { user } = useAuthStore();
  const queryClient = useQueryClient();
  const normalizeWeek = (value) => getWeekStartDate(value).format('YYYY-MM-DD');
  const [weekStart, setWeekStart] = useState(normalizeWeek());
  const [reportDirty, setReportDirty] = useState(false);
  const [dialogState, setDialogState] = useState({ open: false, initialValues: null });
  const [reportForm, setReportForm] = useState(createEmptyWeeklyReport());

  const safeSetWeekStart = (newWeek) => {
    if (reportDirty) {
      if (!window.confirm('Є незбережені зміни у звіті. Перейти на інший тиждень?')) return;
    }
    setReportDirty(false);
    setWeekStart(newWeek);
  };
  const [isCopying, setIsCopying] = useState(false);
  const [isClearing, setIsClearing] = useState(false);
  const [clearConfirmOpen, setClearConfirmOpen] = useState(false);
  const scheduleQueryKey = useMemo(() => ['schedule-me', weekStart], [weekStart]);

  const studentsQuery = useQuery({
    queryKey: ['tutor-students', user?.id],
    queryFn: () => fetchTutorStudents(user.id),
    enabled: Boolean(user?.id),
    staleTime: 5 * 60 * 1000,
  });

  const scheduleQuery = useQuery({
    queryKey: scheduleQueryKey,
    queryFn: () => fetchMySchedules(weekStart),
    enabled: Boolean(user),
    staleTime: 30 * 1000,
    placeholderData: (prev) => prev,
  });

  const weeklyReportQuery = useQuery({
    queryKey: ['weekly-report-me', weekStart],
    queryFn: () => fetchMyWeeklyReport(weekStart),
    enabled: Boolean(user),
    staleTime: 60 * 1000,
    placeholderData: (prev) => prev,
  });

  const weeklyStats = useMemo(() => {
    const stats = {
      completed: 0,
      rescheduled: 0,
      cancelled: 0,
      plannedCompleted: 0,
      plannedStart: scheduleQuery.data?.length || 0,
    };
    (scheduleQuery.data || []).forEach((item) => {
      if (item.status === 'COMPLETED') {
        stats.completed += 1;
        stats.plannedCompleted += 1;
      }
      if (item.status === 'RESCHEDULED') stats.rescheduled += 1;
      if (item.status === 'CANCELLED') stats.cancelled += 1;
    });
    return stats;
  }, [scheduleQuery.data]);

  const invalidateScheduleData = (statusChanged) => {
    queryClient.invalidateQueries({ queryKey: ['schedule-me'] });
    queryClient.invalidateQueries({ queryKey: ['schedule-attention-count'] });
    if (statusChanged) {
      queryClient.invalidateQueries({ queryKey: ['tutor-students'] });
    }
  };

  const createMutation = useMutation({
    mutationFn: createScheduleSlot,
    onSuccess: (_, variables) => {
      enqueueSnackbar('Заняття додано', { variant: 'success' });
      invalidateScheduleData(variables?.status === 'COMPLETED');
    },
    onError: () => enqueueSnackbar('Не вдалося додати заняття', { variant: 'error' }),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, payload }) => updateScheduleSlot(id, payload),
    onSuccess: (_, { payload }) => {
      enqueueSnackbar('Заняття оновлено', { variant: 'success' });
      invalidateScheduleData(Boolean(payload?.status));
    },
    onError: () => enqueueSnackbar('Не вдалося оновити заняття', { variant: 'error' }),
  });

  const deleteMutation = useMutation({
    mutationFn: deleteScheduleSlot,
    onSuccess: () => {
      enqueueSnackbar('Заняття видалено', { variant: 'success' });
      invalidateScheduleData(true);
    },
    onError: () => enqueueSnackbar('Не вдалося видалити заняття', { variant: 'error' }),
  });

  const reportMutation = useMutation({
    mutationFn: submitWeeklyReport,
    onSuccess: () => {
      enqueueSnackbar('Звіт збережено', { variant: 'success' });
      setReportDirty(false);
      queryClient.invalidateQueries({ queryKey: ['weekly-report-me'] });
    },
    onError: () => enqueueSnackbar('Не вдалося зберегти звіт', { variant: 'error' }),
  });

  useEffect(() => {
    if (weeklyReportQuery.data) {
      setReportForm({
        lessonsLoadLevel: weeklyReportQuery.data.lessonsLoadLevel ?? 50,
        preparationLoadLevel: weeklyReportQuery.data.preparationLoadLevel ?? 50,
        studentEngagementLevel: weeklyReportQuery.data.studentEngagementLevel ?? 50,
        attendanceSatisfactionLevel: weeklyReportQuery.data.attendanceSatisfactionLevel ?? 50,
        studentMotivationLevel: weeklyReportQuery.data.studentMotivationLevel ?? 50,
        wellbeingLevel: weeklyReportQuery.data.wellbeingLevel ?? 50,
        attentionStudentIds: weeklyReportQuery.data.attentionStudentIds || [],
        topStudentIds: weeklyReportQuery.data.topStudentIds || [],
        weekMood: weeklyReportQuery.data.weekMood || null,
        comment: weeklyReportQuery.data.comment || '',
      });
    } else if (!weeklyReportQuery.isLoading) {
      setReportForm(createEmptyWeeklyReport());
    }
  }, [weekStart, weeklyReportQuery.data, weeklyReportQuery.isLoading]);

  const handleCellClick = (datetime) => {
    const students = studentsQuery.data || [];
    if (!students.length) {
      enqueueSnackbar('Додайте учнів, щоб створювати заняття', { variant: 'warning' });
      return;
    }
    setDialogState({
      open: true,
      initialValues: {
        tutorId: user.id,
        studentId: students[0]?.id || '',
        startTime: dayjs(datetime).toISOString(),
        endTime: dayjs(datetime).add(1, 'hour').toISOString(),
        status: 'SCHEDULED',
        comment: '',
      },
    });
  };

  const handleEventClick = (event) => {
    setDialogState({
      open: true,
      initialValues: {
        tutorId: event.tutorId,
        studentId: event.studentId,
        startTime: dayjs(event.startTime).toISOString(),
        endTime: dayjs(event.endTime).toISOString(),
        status: event.status,
        comment: event.comment || '',
        id: event.id,
      },
    });
  };

  const handleDialogSubmit = async (values) => {
    const payload = { ...values, tutorId: user.id };
    if (dialogState.initialValues?.id) {
      await updateMutation.mutateAsync({ id: dialogState.initialValues.id, payload });
    } else {
      await createMutation.mutateAsync(payload);
    }
  };

  const studentOptions = useMemo(() => studentsQuery.data || [], [studentsQuery.data]);

  const handleDeleteSchedule = async (id) => {
    await deleteMutation.mutateAsync(id);
    setDialogState({ open: false, initialValues: null });
  };

  const handleEventMove = async (event, newStart, newEnd) => {
    const previousData = queryClient.getQueryData(scheduleQueryKey);
    const startTime = newStart.format('YYYY-MM-DDTHH:mm:ss');
    const endTime = newEnd.format('YYYY-MM-DDTHH:mm:ss');
    queryClient.setQueryData(scheduleQueryKey, (current) => {
      if (!current) return current;
      return current.map((item) => (item.id === event.id ? { ...item, startTime, endTime } : item));
    });
    try {
      await updateMutation.mutateAsync({
        id: event.id,
        payload: {
          startTime,
          endTime,
        },
      });
    } catch (error) {
      queryClient.setQueryData(scheduleQueryKey, previousData);
      throw error;
    }
  };

  const handleCopyPreviousWeek = async () => {
    if (isCopying) return;
    setIsCopying(true);
    try {
      const previousWeekStart = normalizeWeek(dayjs(weekStart).subtract(1, 'week'));
      const previousSchedules = await fetchMySchedules(previousWeekStart);
      if (!previousSchedules.length) {
        enqueueSnackbar('Немає занять за попередній тиждень', { variant: 'info' });
        return;
      }
      const currentWeekEvents = scheduleQuery.data || [];
      const existingKeys = new Set(
        currentWeekEvents.map(
          (item) => `${item.studentId}-${dayjs(item.startTime).format('YYYY-MM-DDTHH:mm:ss')}`
        )
      );
      const payloads = previousSchedules
        .map((item) => {
          const start = dayjs(item.startTime).add(1, 'week');
          const end = dayjs(item.endTime).add(1, 'week');
          const startFormatted = start.format('YYYY-MM-DDTHH:mm:ss');
          return {
            tutorId: user.id,
            studentId: item.studentId,
            startTime: startFormatted,
            endTime: end.format('YYYY-MM-DDTHH:mm:ss'),
            status: 'SCHEDULED',
            comment: item.comment || '',
            key: `${item.studentId}-${startFormatted}`,
          };
        })
        .filter((payload) => !existingKeys.has(payload.key));

      if (!payloads.length) {
        enqueueSnackbar('Поточний тиждень вже містить ці заняття', { variant: 'info' });
        return;
      }

      await Promise.all(
        payloads.map(({ key, ...payload }) => createScheduleSlot(payload))
      );
      enqueueSnackbar('Заняття скопійовані з попереднього тижня', { variant: 'success' });
      invalidateScheduleData();
    } catch (error) {
      enqueueSnackbar('Не вдалося скопіювати розклад', { variant: 'error' });
    } finally {
      setIsCopying(false);
    }
  };

  const handleClearCurrentWeek = () => {
    if (isClearing) return;
    const currentWeekEvents = scheduleQuery.data || [];
    if (!currentWeekEvents.length) {
      enqueueSnackbar('За цей тиждень немає занять', { variant: 'info' });
      return;
    }
    setClearConfirmOpen(true);
  };

  const handleConfirmClear = async () => {
    setClearConfirmOpen(false);
    setIsClearing(true);
    try {
      const currentWeekEvents = scheduleQuery.data || [];
      await Promise.all(currentWeekEvents.map((event) => deleteScheduleSlot(event.id)));
      enqueueSnackbar('Заняття тижня видалено', { variant: 'success' });
      invalidateScheduleData();
    } catch (error) {
      enqueueSnackbar('Не вдалося видалити заняття', { variant: 'error' });
    } finally {
      setIsClearing(false);
    }
  };

  const handleReportFieldChange = (field, value) => {
    setReportForm((prev) => ({ ...prev, [field]: value }));
    setReportDirty(true);
  };

  const handleAttentionStudentsChange = (list) => {
    setReportForm((prev) => ({ ...prev, attentionStudentIds: list }));
    setReportDirty(true);
  };

  const handleTopStudentsChange = (list) => {
    setReportForm((prev) => ({ ...prev, topStudentIds: list }));
    setReportDirty(true);
  };

  const handleReportSubmit = async () => {
    await reportMutation.mutateAsync({
      weekStart,
      ...reportForm,
      attentionStudentIds: reportForm.attentionStudentIds || [],
      topStudentIds: reportForm.topStudentIds || [],
    });
  };

  const B = { primary: '#4357AD', dark: '#1E2A4A' };
  const cardSx = { borderRadius: 4, boxShadow: '0 1px 6px rgba(0,0,0,0.04)', bgcolor: '#fff' };

  return (
    <Stack spacing={2.5}>
      {/* Header + Controls */}
      <Card sx={{ ...cardSx, background: `linear-gradient(135deg, ${B.dark} 0%, ${B.primary} 100%)`, overflow: 'hidden' }}>
        <CardContent sx={{ py: 2.5 }}>
          <Stack direction={{ xs: 'column', sm: 'row' }} alignItems={{ sm: 'center' }} justifyContent="space-between" gap={1.5}>
            <Typography variant="h5" fontWeight={800} sx={{ color: '#fff' }}>Розклад</Typography>
            <Stack direction="row" spacing={0.5} alignItems="center" flexWrap="wrap" useFlexGap>
              <IconButton
                size="small"
                onClick={() => safeSetWeekStart(normalizeWeek(dayjs(weekStart).subtract(1, 'week')))}
                sx={{ color: '#fff', bgcolor: 'rgba(255,255,255,0.15)', '&:hover': { bgcolor: 'rgba(255,255,255,0.25)' } }}
              >
                <ChevronLeftIcon />
              </IconButton>
              <Button
                variant="outlined"
                size="small"
                onClick={() => safeSetWeekStart(normalizeWeek(dayjs()))}
                sx={{ borderColor: 'rgba(255,255,255,0.3)', color: '#fff', borderRadius: 3, textTransform: 'none', fontWeight: 600, fontSize: { xs: 12, sm: 14 }, px: { xs: 1.5, sm: 2 }, '&:hover': { borderColor: '#fff', bgcolor: 'rgba(255,255,255,0.1)' } }}
              >
                Поточний тиждень
              </Button>
              <IconButton
                size="small"
                onClick={() => safeSetWeekStart(normalizeWeek(dayjs(weekStart).add(1, 'week')))}
                sx={{ color: '#fff', bgcolor: 'rgba(255,255,255,0.15)', '&:hover': { bgcolor: 'rgba(255,255,255,0.25)' } }}
              >
                <ChevronRightIcon />
              </IconButton>
              <Box sx={{ display: { xs: 'none', md: 'block' }, width: '1px', height: 28, bgcolor: 'rgba(255,255,255,0.2)' }} />
              <Box sx={{ '& .MuiButton-root': { borderColor: 'rgba(255,255,255,0.3)', color: '#fff', '&:hover': { borderColor: '#fff', bgcolor: 'rgba(255,255,255,0.1)' } }, '& .MuiSvgIcon-root': { color: 'rgba(255,255,255,0.7)' } }}>
                <WeekSelector value={weekStart} onChange={safeSetWeekStart} />
              </Box>
              <Box sx={{ display: { xs: 'none', md: 'block' }, width: '1px', height: 28, bgcolor: 'rgba(255,255,255,0.2)' }} />
              <Tooltip title="Скопіювати з попереднього тижня">
                <span>
                  <IconButton size="small" onClick={handleCopyPreviousWeek} disabled={isCopying}
                    sx={{ color: 'rgba(255,255,255,0.8)', '&:hover': { bgcolor: 'rgba(255,255,255,0.15)', color: '#fff' } }}>
                    <ContentCopyIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title="Видалити всі заняття поточного тижня">
                <span>
                  <IconButton size="small" onClick={handleClearCurrentWeek} disabled={isClearing}
                    sx={{ color: 'rgba(255,255,255,0.8)', '&:hover': { bgcolor: 'rgba(255,82,27,0.2)', color: '#FF521B' } }}>
                    <DeleteSweepIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
            </Stack>
          </Stack>
        </CardContent>
      </Card>

      {/* Calendar */}
      <Card sx={cardSx}>
        <CardContent>
          <Box sx={{ position: 'relative' }}>
            {scheduleQuery.isFetching && !scheduleQuery.data && (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                <CircularProgress sx={{ color: B.primary }} />
              </Box>
            )}
            {(scheduleQuery.data || !scheduleQuery.isFetching) && (
              <>
                {scheduleQuery.isFetching && (
                  <Box sx={{ position: 'absolute', top: 8, right: 8, zIndex: 10 }}>
                    <CircularProgress size={20} sx={{ color: B.primary }} />
                  </Box>
                )}
                <Suspense fallback={<Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress sx={{ color: B.primary }} /></Box>}>
                  <WeeklyCalendar
                    weekStart={weekStart}
                    events={scheduleQuery.data}
                    onCellClick={handleCellClick}
                    onEventClick={handleEventClick}
                    onEventMove={handleEventMove}
                  />
                </Suspense>
              </>
            )}
          </Box>
        </CardContent>
      </Card>

      {/* Stats */}
      <Grid container spacing={1.5}>
        {[
          { label: 'Заплановано', value: Math.max(weeklyStats.plannedStart - weeklyStats.rescheduled, 0), color: B.primary, bg: '#EDE9FE' },
          { label: 'Проведено', value: weeklyStats.completed, color: '#22C55E', bg: '#D1FAE5' },
          { label: 'Перенесено', value: weeklyStats.rescheduled, color: '#F59E0B', bg: '#FEF3C7' },
          { label: 'Скасовано', value: weeklyStats.cancelled, color: '#EF4444', bg: '#FEE2E2' },
          { label: 'Заплан→провед', value: weeklyStats.plannedCompleted, color: '#06B6D4', bg: '#CFFAFE' },
        ].map((s) => (
          <Grid item xs={4} sm={true} key={s.label} sx={{ flexGrow: 1 }}>
            <Card sx={{ ...cardSx, height: '100%' }}>
              <CardContent sx={{ py: 2.5, textAlign: 'center' }}>
                <Typography variant="h5" fontWeight={800} sx={{ color: s.color, mb: 0.5 }}>{s.value}</Typography>
                <Typography variant="body2" sx={{ color: '#8891A5', fontWeight: 500 }}>{s.label}</Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <WeeklyReportPanel
        title="Тижневий стан викладача"
        subtitle={`Тиждень ${formatWeekRangeLabel(weekStart)}`}
        values={reportForm}
        onFieldChange={handleReportFieldChange}
        onMoodChange={(value) => handleReportFieldChange('weekMood', value)}
        onAttentionChange={handleAttentionStudentsChange}
        onTopStudentsChange={handleTopStudentsChange}
        onCommentChange={(value) => handleReportFieldChange('comment', value)}
        onSubmit={handleReportSubmit}
        submitting={reportMutation.isPending}
        studentOptions={studentOptions}
        updatedAt={weeklyReportQuery.data?.updatedAt}
      />

      <ConfirmDialog
        open={clearConfirmOpen}
        title="Видалити всі заняття тижня?"
        description="Усі заняття поточного тижня буде видалено. Цю дію відмінити неможливо."
        confirmText="Видалити"
        onCancel={() => setClearConfirmOpen(false)}
        onConfirm={handleConfirmClear}
      />

      <ScheduleFormDialog
        open={dialogState.open}
        onClose={() => setDialogState({ open: false, initialValues: null })}
        onSubmit={handleDialogSubmit}
        onDelete={
          dialogState.initialValues?.id
            ? () => handleDeleteSchedule(dialogState.initialValues.id)
            : undefined
        }
        tutors={[{ id: user.id, name: user.name }]}
        students={studentOptions}
        initialValues={dialogState.initialValues}
        title={dialogState.initialValues?.id ? 'Редагувати заняття' : 'Нове заняття'}
      />
    </Stack>
  );
};

export default TutorSchedulePage;

